import { Fragment, useState } from 'react'
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  BedDouble,
  CalendarDays,
  ClipboardList,
  ClipboardPaste,
  FileSearch,
  FilePlus2,
  GitMerge,
  Heart,
  Pencil,
  Pill,
  Plus,
  ShieldAlert,
  Stethoscope,
  Baby,
  Ribbon,
  Lightbulb,
} from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import type { CategorieDonnee, Donnee, Dossier as DossierComplet, ModeDossier } from '../lib/types'
import { Carte, Chargement, EtatVide, Message, Modale, Pastille } from '../composants/base'
import LigneDonnee from '../composants/dossier/LigneDonnee'
import FormulaireDonnee from '../composants/dossier/FormulaireDonnee'
import Timeline from '../composants/dossier/Timeline'
import Venues from '../composants/dossier/Venues'
import Documents from '../composants/dossier/Documents'
import Biologie from '../composants/dossier/Biologie'
import Cancer from '../composants/dossier/Cancer'
import Identite from '../composants/dossier/Identite'
import Conflits from '../composants/dossier/Conflits'
import Fusion from '../composants/dossier/Fusion'
import * as f from '../lib/format'
import { MODES_DOSSIER, SEXES, STATUTS_TRAITEMENT, TYPES_ANTECEDENT } from '@doc/shared'
import { COULEURS_MODE, styleMode } from '../lib/couleurs'
import { estOncologique } from '@doc/shared'

type CleCarte =
  | 'cancer'
  | 'diagnostics'
  | 'traitements'
  | 'antecedents'
  | 'allergies'
  | 'specifiques'
  | 'biologie'
  | 'timeline'
  | 'venues'
  | 'documents'

// Le mode change ce qui est mis en avant et l'agencement des cartes. Les
// données, elles, sont les mêmes (critère d'acceptation 9).
const DISPOSITIONS: Record<ModeDossier, { principale: CleCarte[]; laterale: CleCarte[] }> = {
  GYNECOLOGIE_BENIGNE: {
    principale: ['diagnostics', 'traitements', 'biologie', 'timeline', 'venues'],
    laterale: ['allergies', 'antecedents', 'documents'],
  },
  OBSTETRIQUE: {
    principale: ['specifiques', 'venues', 'biologie', 'timeline', 'diagnostics'],
    laterale: ['allergies', 'traitements', 'antecedents', 'documents'],
  },
  // En oncologie, la carte « Cancer » passe devant tout le reste.
  ONCOLOGIE: {
    principale: ['cancer', 'biologie', 'traitements', 'timeline', 'venues'],
    laterale: ['allergies', 'diagnostics', 'antecedents', 'documents'],
  },
}

// Les antécédents sont groupés par nature : le mot « Chirurgical » devient un
// titre de groupe au lieu d'être répété sur chaque ligne.
const ORDRE_ANTECEDENTS = [
  'CHIRURGICAL',
  'GYNECOLOGIQUE',
  'OBSTETRICAL',
  'MEDICAL',
  'FAMILIAL',
  'MODE_DE_VIE',
] as const

// Les traitements sont groupés par statut : « Arrêté » devient un titre de
// groupe au lieu d'une pastille répétée sur chaque ligne.
const ORDRE_TRAITEMENTS = ['ACTUEL', 'ARRETE', 'ANCIEN'] as const

const CARTES: Record<
  Exclude<CleCarte, 'timeline' | 'venues' | 'documents' | 'biologie' | 'cancer'>,
  { titre: string; categorie: CategorieDonnee; icone: React.ReactNode; vide: string }
> = {
  diagnostics: {
    titre: 'Diagnostics',
    categorie: 'DIAGNOSTIC',
    icone: <Stethoscope className="h-[17px] w-[17px] text-bleu-500" />,
    vide: 'Aucun diagnostic. Un patient peut en porter plusieurs en même temps.',
  },
  traitements: {
    titre: 'Traitements',
    categorie: 'TRAITEMENT',
    icone: <Pill className="h-[17px] w-[17px] text-bleu-500" />,
    vide: 'Aucun traitement enregistré.',
  },
  antecedents: {
    titre: 'Antécédents',
    categorie: 'ANTECEDENT',
    icone: <Heart className="h-[17px] w-[17px] text-bleu-500" />,
    vide: 'Aucun antécédent enregistré.',
  },
  allergies: {
    titre: 'Allergies',
    categorie: 'ALLERGIE',
    icone: <ShieldAlert className="h-[17px] w-[17px] text-critique" />,
    vide: 'Aucune allergie connue. Cela ne veut pas dire qu’il n’y en a pas : rien n’a été saisi.',
  },
  specifiques: {
    titre: 'Données spécifiques',
    categorie: 'DONNEE_SPECIFIQUE',
    icone: <Activity className="h-[17px] w-[17px] text-bleu-500" />,
    vide: 'Rien de saisi.',
  },
}

export default function Dossier() {
  const { id } = useParams<{ id: string }>()
  const [parametres, setParametres] = useSearchParams()
  const client = useQueryClient()
  const naviguer = useNavigate()

  const [identiteOuverte, setIdentiteOuverte] = useState(parametres.get('identite') === '1')
  const [fusionOuverte, setFusionOuverte] = useState(false)
  const [ajout, setAjout] = useState<CategorieDonnee | null>(null)
  const [hospitalisation, setHospitalisation] = useState<string | null>(null)

  const action = parametres.get('action')
  const documentDemande = parametres.get('document')
  const conflitDemande = parametres.get('conflit')

  const effacerParametres = () => setParametres({}, { replace: true })

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dossier', id],
    queryFn: () => api<DossierComplet>(`/api/patients/${id}`),
    enabled: Boolean(id),
  })

  const changerMode = useMutation({
    mutationFn: (mode: ModeDossier) =>
      api(`/api/patients/${id}/mode`, { methode: 'PUT', corps: { modeAffichage: mode } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['dossier', id] }),
  })

  const ecarterSuggestion = useMutation({
    mutationFn: (mode: ModeDossier) =>
      api(`/api/patients/${id}/mode/ecarter`, {
        methode: 'POST',
        corps: { modeAffichage: mode },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['dossier', id] }),
  })

  const hospitaliser = useMutation({
    mutationFn: () => api(`/api/patients/${id}/hospitaliser`, { methode: 'POST', corps: {} }),
    onError: (e) => setHospitalisation(messageErreur(e)),
    onSuccess: () => setHospitalisation(null),
  })

  if (isLoading) return <Chargement texte="Ouverture du dossier…" />
  if (isError || !data)
    return (
      <div className="mx-auto max-w-xl pt-10">
        <Message ton="critique">{messageErreur(error)}</Message>
      </div>
    )

  const { patient, cartes, propositions, venues, documents, conflits, timeline } = data
  const suggestion = data.suggestionMode
  const disposition = DISPOSITIONS[patient.modeAffichage]

  // En oncologie, le cancer a sa carte : les autres diagnostics restent dans la
  // leur, sans être écrits deux fois.
  const oncologique = patient.modeAffichage === 'ONCOLOGIE'
  const diagnosticsCancer = oncologique
    ? cartes.diagnostics.filter((d) => estOncologique(d.libelle))
    : []
  const caracteristiquesCancer = oncologique
    ? cartes.specifiques.filter((d) => d.detail?.domaine === 'oncologie')
    : []

  const donneesDe = (
    cle: Exclude<CleCarte, 'timeline' | 'venues' | 'documents' | 'biologie' | 'cancer'>,
  ): Donnee[] => {
    switch (cle) {
      case 'diagnostics':
        return oncologique
          ? cartes.diagnostics.filter((d) => !estOncologique(d.libelle))
          : cartes.diagnostics
      case 'traitements':
        return cartes.traitements
      case 'antecedents':
        return cartes.antecedents
      case 'allergies':
        return cartes.allergies
      case 'specifiques':
        return oncologique
          ? cartes.specifiques.filter((d) => d.detail?.domaine !== 'oncologie')
          : cartes.specifiques
    }
  }

  const titreSpecifique =
    patient.modeAffichage === 'OBSTETRIQUE'
      ? 'Grossesse et obstétrique'
      : patient.modeAffichage === 'ONCOLOGIE'
        ? 'Oncologie'
        : 'Données spécifiques'

  const rendreCarte = (cle: CleCarte) => {
    if (cle === 'timeline') {
      return (
        <Timeline
          key="timeline"
          evenements={timeline}
          actions={
            <button
              className="bouton-discret px-2"
              onClick={() => setAjout('EVENEMENT_HISTOIRE')}
              title="Ajouter un événement"
            >
              <Plus className="h-4 w-4" />
            </button>
          }
        />
      )
    }
    if (cle === 'venues') {
      return (
        <Venues
          key="venues"
          patientId={patient.id}
          venues={venues}
          diagnostics={cartes.diagnostics}
          ouvrirFormulaire={action === 'venue'}
          surFermeture={effacerParametres}
        />
      )
    }
    if (cle === 'cancer') {
      return (
        <Cancer
          key="cancer"
          patientId={patient.id}
          diagnostics={diagnosticsCancer}
          caracteristiques={caracteristiquesCancer}
        />
      )
    }
    if (cle === 'biologie') {
      return <Biologie key="biologie" patientId={patient.id} />
    }
    if (cle === 'documents') {
      return (
        <Documents
          key="documents"
          patientId={patient.id}
          documents={documents}
          venues={venues}
          ouvrir={action === 'document' ? 'document' : action === 'texte' ? 'texte' : null}
          documentDemande={documentDemande}
          surFermeture={effacerParametres}
          surDepot={(retour) => {
            // Un document analysé mène droit à l'écran de vérification.
            if (retour.analyseId) naviguer(`/verification/${retour.analyseId}`)
          }}
        />
      )
    }

    const config = CARTES[cle]
    const liste = donneesDe(cle)
    return (
      <Carte
        key={cle}
        titre={
          cle === 'specifiques'
            ? titreSpecifique
            : cle === 'diagnostics' && oncologique
              ? 'Autres diagnostics'
              : config.titre
        }
        icone={
          cle === 'specifiques' ? (
            patient.modeAffichage === 'OBSTETRIQUE' ? (
              <Baby className="h-[17px] w-[17px] text-bleu-500" />
            ) : patient.modeAffichage === 'ONCOLOGIE' ? (
              <Ribbon className="h-[17px] w-[17px] text-bleu-500" />
            ) : (
              config.icone
            )
          ) : (
            config.icone
          )
        }
        compte={liste.length}
        actions={
          <button
            className="bouton-discret px-2"
            onClick={() => setAjout(config.categorie)}
            title="Ajouter"
          >
            <Plus className="h-4 w-4" />
          </button>
        }
      >
        {cle === 'specifiques' && liste.length === 0 ? (
          <div className="px-5 pb-5">
            <Message ton="info">
              Les champs précis des modes obstétrique et oncologie ne sont pas arrêtés : rien n’est
              imposé, ni terme, ni stade, ni TNM. En attendant, tu peux saisir librement un
              intitulé et sa valeur.
            </Message>
          </div>
        ) : liste.length === 0 ? (
          <EtatVide
            texte={config.vide}
            action={
              <button className="bouton-doux mt-1" onClick={() => setAjout(config.categorie)}>
                <Plus className="h-4 w-4" />
                Ajouter
              </button>
            }
          />
        ) : cle === 'traitements' ? (
          <ul className="pb-2">
            {ORDRE_TRAITEMENTS.map((statut) => {
              const groupe = liste.filter(
                (donnee) => (donnee.detail?.statut ?? 'ACTUEL') === statut,
              )
              if (groupe.length === 0) return null
              return (
                <Fragment key={statut}>
                  <li className="border-t border-trait px-5 pt-3 pb-0.5 text-[11px] font-medium tracking-wide text-brume uppercase first:border-t-0">
                    {STATUTS_TRAITEMENT[statut]}
                  </li>
                  {groupe.map((donnee) => (
                    <LigneDonnee key={donnee.id} donnee={donnee} patientId={patient.id} />
                  ))}
                </Fragment>
              )
            })}
          </ul>
        ) : cle === 'antecedents' ? (
          <ul className="pb-2">
            {ORDRE_ANTECEDENTS.map((type) => {
              const groupe = liste.filter(
                (donnee) => (donnee.detail?.type ?? 'MEDICAL') === type,
              )
              if (groupe.length === 0) return null
              return (
                <Fragment key={type}>
                  <li className="border-t border-trait px-5 pt-3 pb-0.5 text-[11px] font-medium tracking-wide text-brume uppercase first:border-t-0">
                    {TYPES_ANTECEDENT[type]}
                  </li>
                  {groupe.map((donnee) => (
                    <LigneDonnee
                      key={donnee.id}
                      donnee={donnee}
                      patientId={patient.id}
                      sansType
                    />
                  ))}
                </Fragment>
              )
            })}
          </ul>
        ) : (
          <ul className="divide-y divide-trait pb-2">
            {liste.map((donnee) => (
              <LigneDonnee key={donnee.id} donnee={donnee} patientId={patient.id} />
            ))}
          </ul>
        )}
      </Carte>
    )
  }

  return (
    <div className="mx-auto max-w-[1180px] pt-1">
      {/* Identité d'abord, avec la couleur du type de dossier */}
      <div
        className="carte mb-5 overflow-hidden px-6 py-5"
        style={{ borderLeft: `4px solid ${COULEURS_MODE[patient.modeAffichage].accent}` }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[21px] font-semibold tracking-tight">{patient.affichage}</h1>
              {/* La référence n'est répétée que si le nom ne la contient pas déjà. */}
              {patient.nom && <Pastille>{patient.reference}</Pastille>}
              {patient.fusionneVers && (
                <Pastille ton="attention" icone={<GitMerge className="h-3 w-3" />}>
                  Fusionné dans un autre dossier
                </Pastille>
              )}
            </div>

            <p className="mt-1 text-[13px] text-ardoise">
              {patient.dateNaissance
                ? `Née le ${f.dateCourte(patient.dateNaissance)} · ${f.age(patient.dateNaissance)}`
                : 'Date de naissance non renseignée'}
              {' · '}
              {SEXES[patient.sexe as keyof typeof SEXES]}
              {patient.telephone ? ` · ${patient.telephone}` : ''}
              {patient.email ? ` · ${patient.email}` : ''}
            </p>
            {patient.adresse && <p className="text-[13px] text-brume">{patient.adresse}</p>}
            {patient.notes && (
              <p className="mt-1 text-[13px] text-ardoise italic">{patient.notes}</p>
            )}

            {patient.fusionneVers && (
              <p className="mt-2 text-[13px]">
                Ce dossier a été absorbé par{' '}
                <Link
                  className="font-medium text-bleu-600 hover:underline"
                  to={`/patients/${patient.fusionneVers.id}`}
                >
                  {patient.fusionneVers.reference}
                </Link>
                .
              </p>
            )}
            {patient.fusionnes.length > 0 && (
              <p className="mt-2 text-[12.5px] text-brume">
                Dossiers absorbés : {patient.fusionnes.map((p) => p.reference).join(', ')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Le sélecteur porte la couleur du type : il nomme le dossier et
                permet d'en changer, sans que l'information soit écrite deux fois. */}
            <select
              className="champ w-[205px] font-medium"
              style={{
                ...styleMode(patient.modeAffichage),
                borderColor: COULEURS_MODE[patient.modeAffichage].accent,
              }}
              value={patient.modeAffichage}
              onChange={(e) => changerMode.mutate(e.target.value as ModeDossier)}
              title="Présentation du dossier, mémorisée pour ce patient"
            >
              {Object.entries(MODES_DOSSIER).map(([cle, libelle]) => (
                <option key={cle} value={cle}>
                  {libelle}
                </option>
              ))}
            </select>
            <button className="bouton-discret" onClick={() => setIdentiteOuverte(true)}>
              <Pencil className="h-4 w-4" />
              Identité
            </button>
            <button className="bouton-discret" onClick={() => setFusionOuverte(true)}>
              <GitMerge className="h-4 w-4" />
              Fusionner
            </button>
            <button className="bouton-discret" onClick={() => hospitaliser.mutate()}>
              <BedDouble className="h-4 w-4" />
              Hospitaliser
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-trait pt-4">
          <button
            className="bouton-doux"
            onClick={() => setParametres({ action: 'venue' }, { replace: true })}
          >
            <CalendarDays className="h-4 w-4" />
            Nouvelle venue
          </button>
          <button
            className="bouton-doux"
            onClick={() => setParametres({ action: 'document' }, { replace: true })}
          >
            <FilePlus2 className="h-4 w-4" />
            Ajouter un document
          </button>
          <button
            className="bouton-doux"
            onClick={() => setParametres({ action: 'texte' }, { replace: true })}
          >
            <ClipboardPaste className="h-4 w-4" />
            Coller un texte
          </button>
        </div>
      </div>

      {/* Reconnaissance du type de dossier : une proposition, jamais un
          changement d'office, et elle dit toujours sur quoi elle se fonde. */}
      {suggestion && (
        <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[14px] bg-bleu-50 px-5 py-3.5">
          <Lightbulb className="h-[18px] w-[18px] shrink-0 text-bleu-600" />
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] text-encre">
              Ce dossier ressemble à un dossier{' '}
              <span className="font-medium">
                « {MODES_DOSSIER[suggestion.mode].toLowerCase()} »
              </span>
              .
            </p>
            <p className="mt-0.5 text-[12.5px] text-ardoise">
              D’après {suggestion.raisons.join(', ')}.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              className="bouton-discret"
              onClick={() => ecarterSuggestion.mutate(suggestion.mode)}
            >
              Non, garder celui-ci
            </button>
            <button
              className="bouton-primaire"
              onClick={() => changerMode.mutate(suggestion.mode)}
              disabled={changerMode.isPending}
            >
              Passer en {MODES_DOSSIER[suggestion.mode].toLowerCase()}
            </button>
          </div>
        </div>
      )}

      {/* Allergies : toujours visibles, avant le reste */}
      {cartes.allergies.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[14px] bg-critique-fond px-5 py-3">
          <span className="flex items-center gap-2 text-[13px] font-semibold text-critique">
            <ShieldAlert className="h-4 w-4" />
            Allergies
          </span>
          {cartes.allergies.map((a) => (
            <span key={a.id} className="text-[13.5px] text-encre">
              {a.libelle}
              {a.detail?.reaction ? ` (${String(a.detail.reaction)})` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Alertes ensuite : désaccords, puis propositions à vérifier */}
      <Conflits conflits={conflits} patientId={patient.id} conflitDemande={conflitDemande} />

      {propositions.length > 0 && (
        <div className="mb-5">
          <Carte
            titre="À vérifier avant d’entrer au dossier"
            icone={<ClipboardList className="h-[17px] w-[17px] text-attention" />}
            compte={propositions.length}
            actions={
              // Vérifier avec le document sous les yeux est plus sûr que de
              // valider à l'aveugle depuis le dossier.
              [...new Set(propositions.map((p) => p.analyseId).filter(Boolean))].map((analyseId) => (
                <Link key={analyseId} to={`/verification/${analyseId}`} className="bouton-doux">
                  <FileSearch className="h-4 w-4" />
                  Vérifier avec le document
                </Link>
              ))
            }
          >
            <div className="px-5 pb-2">
              <Message ton="attention">
                Ces informations viennent d’une analyse de document. Elles ne font pas partie du
                dossier avant ta validation.
              </Message>
            </div>
            <ul className="divide-y divide-trait pb-2">
              {propositions.map((donnee) => (
                <LigneDonnee key={donnee.id} donnee={donnee} patientId={patient.id} propose />
              ))}
            </ul>
          </Carte>
        </div>
      )}

      {/* Contexte médical, chronologie, actions */}
      {/* min-w-0 sur les deux colonnes : sans lui, une carte au contenu large
          déborde de sa part et écrase l'autre colonne. */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.55fr_1fr]">
        <div className="flex min-w-0 flex-col gap-5">{disposition.principale.map(rendreCarte)}</div>
        <div className="flex min-w-0 flex-col gap-5">{disposition.laterale.map(rendreCarte)}</div>
      </div>

      {identiteOuverte && (
        <Identite
          patient={patient}
          fermer={() => {
            setIdentiteOuverte(false)
            effacerParametres()
          }}
        />
      )}

      {fusionOuverte && <Fusion patientId={patient.id} fermer={() => setFusionOuverte(false)} />}

      {ajout && (
        <FormulaireDonnee
          patientId={patient.id}
          categorie={ajout}
          fermer={() => setAjout(null)}
        />
      )}

      {hospitalisation && (
        <Modale
          titre="Hospitalisation"
          fermer={() => setHospitalisation(null)}
          piedDePage={
            <button className="bouton-primaire" onClick={() => setHospitalisation(null)}>
              J’ai compris
            </button>
          }
        >
          <Message ton="attention">
            <p className="font-medium">Rien n’a été transmis.</p>
            <p className="mt-1">{hospitalisation}</p>
            <p className="mt-1">
              Ce bouton créera le dossier dans ton application de transmissions des hospitalisations
              (cgfl.eislert.fr) une fois son interface définie. C’est la phase 6.
            </p>
          </Message>
        </Modale>
      )}
    </div>
  )
}
